import { useMemo, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Eye, Filter, Calendar, Clock, Phone, MapPin, X, CheckCircle2, DollarSign, Loader, AlertCircle, MessageCircle, Search, ChevronDown, ChevronUp } from 'lucide-react'
import { collection, getDocs, doc, updateDoc, getDoc, query, orderBy, onSnapshot, limit, startAfter } from 'firebase/firestore'
import { db, COLLECTIONS, app } from '../firebase/config'
import PageHelpButton from '../components/PageHelpButton'
import { getFunctions, httpsCallable } from 'firebase/functions'
import toast from 'react-hot-toast'

// Helper functions for date/time calculations
function toMinutes(hhmm) {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

function overlaps(startA, endA, startB, endB) {
  return startA < endB && startB < endA
}

function getWeekdayName(dateStr) {
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, { weekday: 'long' })
}

function pad(n) {
  return String(n).padStart(2, '0')
}

function minutesToTime(mins) {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${pad(h)}:${pad(m)}`
}

// Helper to normalise Ghana phone numbers for SMS (233 + 9 digits)
function normalizeGhanaPhone(rawPhone) {
  if (!rawPhone) return ''
  let phone = rawPhone.toString()

  // Pattern 1: 0XXXXXXXXX
  const pattern1 = phone.match(/0\d{9}/)
  if (pattern1) {
    return '233' + pattern1[0].substring(1)
  }

  // Pattern 2: 233XXXXXXXXX
  const pattern2 = phone.match(/233\d{9}/)
  if (pattern2) {
    return pattern2[0]
  }

  // Pattern 3: +233XXXXXXXXX
  const pattern3 = phone.match(/\+233\d{9}/)
  if (pattern3) {
    return pattern3[0].substring(1)
  }

  // Fallback: first 9-12 digits
  const digits = phone.match(/\d{9,12}/)
  if (digits) {
    let candidate = digits[0]
    if (candidate.startsWith('0')) {
      candidate = '233' + candidate.substring(1)
    } else if (!candidate.startsWith('233')) {
      candidate = '233' + candidate
    }
    if (candidate.length === 12 && candidate.startsWith('233')) {
      return candidate
    }
  }

  return ''
}

const PAGE_SIZE = 50

const Bookings = () => {
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [lastVisible, setLastVisible] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [activeBooking, setActiveBooking] = useState(null)

  // Search (booking code, name, phone, etc.)
  const [searchQuery, setSearchQuery] = useState('')

  // Filters
  const [filterCategory, setFilterCategory] = useState('All')
  const [filterStatus, setFilterStatus] = useState('All')
  const [filterDate, setFilterDate] = useState('')
  const [filterPayment, setFilterPayment] = useState('All')
  const [filterOpen, setFilterOpen] = useState(false)

  // Reschedule date/time state
  const [settings, setSettings] = useState({ postBookingIntervalMinutes: 120, rules: [] })
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [rescheduleTime, setRescheduleTime] = useState('')
  const [availableDates, setAvailableDates] = useState([])
  const [availableTimes, setAvailableTimes] = useState([])

  // Button / modal loading states
  const [loadingMarkCompleted, setLoadingMarkCompleted] = useState(false)
  const [loadingMarkAsPaid, setLoadingMarkAsPaid] = useState(false)
  const [loadingReschedule, setLoadingReschedule] = useState(false)
  const [loadingReviewLink, setLoadingReviewLink] = useState(false)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)

  const filtered = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase()
    return bookings.filter(b => {
      if (q) {
        const matchId = (b.id || '').toLowerCase().includes(q)
        const matchName = (b.name || '').toLowerCase().includes(q)
        const matchPhone = (b.phone || '').replace(/\s/g, '').includes(q.replace(/\s/g, ''))
        const matchCategory = (b.category || '').toLowerCase().includes(q)
        const matchService = (b.serviceType || '').toLowerCase().includes(q)
        if (!matchId && !matchName && !matchPhone && !matchCategory && !matchService) return false
      }
      const byCat = filterCategory === 'All' || b.category === filterCategory
      const byStatus = filterStatus === 'All' || b.status === filterStatus
      const byDate = !filterDate || b.date === filterDate
      const byPayment = filterPayment === 'All' ||
        (filterPayment === 'completed' && b.paymentCompleted) ||
        (filterPayment === 'uncompleted' && !b.paymentCompleted)
      return byCat && byStatus && byDate && byPayment
    })
  }, [bookings, searchQuery, filterCategory, filterStatus, filterDate, filterPayment])

  const openPreview = (booking) => {
    setActiveBooking(booking)
    setRescheduleDate(booking.date || '')
    setRescheduleTime(booking.time || '')
    setShowModal(true)
    
    // Scroll to top of page when modal opens
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const markCompleted = async () => {
    if (!activeBooking || loadingMarkCompleted) return
    
    setLoadingMarkCompleted(true)
    try {
      // Update booking status first
      const success = await updateBookingInFirestore(activeBooking.id, { status: 'completed' })
      if (!success) {
        toast.error('Failed to update booking status')
        setLoadingMarkCompleted(false)
        return
      }
      
      // Update local state
      setBookings(prev => prev.map(b => b.id === activeBooking.id ? { ...b, status: 'completed' } : b))
      setActiveBooking(prev => ({ ...prev, status: 'completed' }))
      
      // Send completion SMS:
      // - If payment is completed, send thank you + review link (0 balance)
      // - If payment is not completed, send current completion message with balance + payment link
      const rawBalance = Number(activeBooking.balance) || 0
      const balanceForSMS = activeBooking.paymentCompleted ? 0 : rawBalance
      const phone = normalizeGhanaPhone(activeBooking.phone)

      if (phone && activeBooking.name) {
        const functionsInstance = getFunctions(app)
        const sendCompletion = httpsCallable(functionsInstance, 'sendCompletionSMS')
        
        await sendCompletion({
          name: activeBooking.name,
          phone,
          balance: balanceForSMS,
          bookingId: activeBooking.id
        })
      }
      
      toast.success('Booking marked as completed and SMS sent')
    } catch (error) {
      console.error('Error in markCompleted:', error)
      toast.error('Failed to send completion SMS, but booking status updated')
    } finally {
      setLoadingMarkCompleted(false)
    }
  }


  const markAsPaid = async () => {
    if (!activeBooking || loadingMarkAsPaid) return
    
    setLoadingMarkAsPaid(true)
    try {
      const balanceTransactionRef = activeBooking.balanceTransactionRef || `TXN-BAL-${Date.now()}`
      const updates = {
        balancePaid: true,
        balanceTransactionRef: balanceTransactionRef,
        paymentCompleted: true
      }
      
      const success = await updateBookingInFirestore(activeBooking.id, updates)
      if (success) {
        setBookings(prev => prev.map(b => 
          b.id === activeBooking.id 
            ? { ...b, ...updates }
            : b
        ))
        setActiveBooking(prev => ({ 
          ...prev, 
          ...updates
        }))

        // Send thank you SMS with review link
        const phone = normalizeGhanaPhone(activeBooking.phone)
        if (phone && activeBooking.name) {
          try {
            const functionsInstance = getFunctions(app)
            const sendPaymentCompletion = httpsCallable(functionsInstance, 'sendPaymentCompletionSMS')
            await sendPaymentCompletion({
              name: activeBooking.name,
              phone,
              bookingId: activeBooking.id
            })
          } catch (smsError) {
            console.error('Error sending payment completion SMS:', smsError)
          }
        }

        toast.success('Balance marked as paid and client thanked')
      }
    } catch (error) {
      console.error('Error in markAsPaid:', error)
      toast.error('Failed to mark as paid')
    } finally {
      setLoadingMarkAsPaid(false)
    }
  }

  const markAsUnpaid = async () => {
    if (!activeBooking || loadingMarkAsPaid) return

    setLoadingMarkAsPaid(true)
    try {
      const updates = {
        balancePaid: false,
        paymentCompleted: false,
        balanceTransactionRef: ''
      }

      const success = await updateBookingInFirestore(activeBooking.id, updates)
      if (success) {
        setBookings(prev => prev.map(b => 
          b.id === activeBooking.id 
            ? { ...b, ...updates }
            : b
        ))
        setActiveBooking(prev => ({ 
          ...prev, 
          ...updates
        }))

        // Notify client that payment status was corrected / reverted
        const phone = normalizeGhanaPhone(activeBooking.phone)
        if (phone && activeBooking.name) {
          try {
            const functionsInstance = getFunctions(app)
            const sendPaymentReverted = httpsCallable(functionsInstance, 'sendPaymentRevertedSMS')
            await sendPaymentReverted({
              name: activeBooking.name,
              phone,
              bookingId: activeBooking.id
            })
          } catch (smsError) {
            console.error('Error sending payment reverted SMS:', smsError)
          }
        }

        toast.success('Payment status marked as unpaid and client notified')
      }
    } catch (error) {
      console.error('Error in markAsUnpaid:', error)
      toast.error('Failed to mark as unpaid')
    } finally {
      setLoadingMarkAsPaid(false)
    }
  }

  const markNotCompleted = async () => {
    if (!activeBooking || loadingMarkCompleted) return

    setLoadingMarkCompleted(true)
    try {
      const updates = { status: 'pending' }
      const success = await updateBookingInFirestore(activeBooking.id, updates)
      if (success) {
        setBookings(prev => prev.map(b =>
          b.id === activeBooking.id
            ? { ...b, ...updates }
            : b
        ))
        setActiveBooking(prev => ({
          ...prev,
          ...updates
        }))

        const phone = normalizeGhanaPhone(activeBooking.phone)
        if (phone && activeBooking.name) {
          try {
            const functionsInstance = getFunctions(app)
            const sendCompletionReverted = httpsCallable(functionsInstance, 'sendCompletionRevertedSMS')
            await sendCompletionReverted({
              name: activeBooking.name,
              phone,
              bookingId: activeBooking.id
            })
          } catch (smsError) {
            console.error('Error sending completion reverted SMS:', smsError)
          }
        }

        toast.success('Service status marked as not completed and client notified')
      }
    } catch (error) {
      console.error('Error in markNotCompleted:', error)
      toast.error('Failed to update service status')
    } finally {
      setLoadingMarkCompleted(false)
    }
  }

  const reschedule = async () => {
    if (!activeBooking || loadingReschedule) return
    
    if (!rescheduleDate || !rescheduleTime) {
      toast.error('Please select both date and time')
      return
    }

    // Validate that the selected time is in available times
    if (!availableTimes.includes(rescheduleTime)) {
      toast.error('Selected time is not available. Please choose from the available times.')
      return
    }
    
    setLoadingReschedule(true)
    try {
      // Update booking in Firestore
      const success = await updateBookingInFirestore(activeBooking.id, { 
        date: rescheduleDate, 
        time: rescheduleTime, 
        status: 'rescheduled' 
      })
      
      if (!success) {
        toast.error('Failed to reschedule booking')
        setLoadingReschedule(false)
        return
      }
      
      // Update local state
      setBookings(prev => prev.map(b => b.id === activeBooking.id ? { ...b, date: rescheduleDate, time: rescheduleTime, status: 'rescheduled' } : b))
      setActiveBooking(prev => ({ ...prev, date: rescheduleDate, time: rescheduleTime, status: 'rescheduled' }))
      
      // Send reschedule SMS and schedule reminders
      const functionsInstance = getFunctions(app)
      const sendReschedule = httpsCallable(functionsInstance, 'sendRescheduleSMS')
      
      await sendReschedule({
        name: activeBooking.name,
        phone: activeBooking.phone,
        date: rescheduleDate,
        time: rescheduleTime,
        serviceType: activeBooking.serviceType,
        category: activeBooking.category,
        visitType: activeBooking.visitType,
        address: activeBooking.address,
        bookingId: activeBooking.id
      })
      
      toast.success('Booking rescheduled successfully and SMS sent')
    } catch (error) {
      console.error('Error in reschedule:', error)
      toast.error('Failed to send reschedule SMS, but booking was updated')
    } finally {
      setLoadingReschedule(false)
    }
  }

  // Maps Firestore snapshot docs to booking objects with defaults
  const mapDocToBooking = (docSnap) => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      name: data.name || '',
      phone: data.phone || '',
      category: data.category || '',
      serviceType: data.serviceType || '',
      price: data.price || 0,
      duration: data.duration || 0,
      bookingFor: data.bookingFor || 1,
      date: data.date || '',
      time: data.time || '',
      visitType: data.visitType || 'spa',
      region: data.region || '',
      address: data.address || '',
      deposit: data.deposit || 0,
      deposited: data.deposited || false,
      depositTransactionRef: data.depositTransactionRef || '',
      balance: data.balance || 0,
      balancePaid: data.balancePaid || false,
      balanceTransactionRef: data.balanceTransactionRef || '',
      paymentCompleted: data.paymentCompleted || false,
      useGiftCard: data.useGiftCard || false,
      payDeposit: data.payDeposit !== undefined ? data.payDeposit : true,
      status: data.status || 'pending',
      createdAt: data.createdAt || '',
      updatedAt: data.updatedAt || ''
    }
  }

  const sortBookings = (bookingsList) => {
    const list = [...bookingsList]
    const now = new Date()

    list.sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)

      if (a.time) {
        const [hoursA, minutesA] = a.time.split(':').map(Number)
        dateA.setHours(hoursA, minutesA, 0, 0)
      }
      if (b.time) {
        const [hoursB, minutesB] = b.time.split(':').map(Number)
        dateB.setHours(hoursB, minutesB, 0, 0)
      }

      const isAFuture = dateA >= now
      const isBFuture = dateB >= now

      if (isAFuture && isBFuture) {
        return dateA - dateB
      }

      if (!isAFuture && !isBFuture) {
        return dateB - dateA
      }

      return isAFuture ? -1 : 1
    })

    return list
  }

  // Realtime listener for latest bookings (top 50)
  useEffect(() => {
    const bookingsRef = collection(db, COLLECTIONS.BOOKINGS)
    const q = query(bookingsRef, orderBy('date', 'desc'), limit(PAGE_SIZE))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const bookingsData = snapshot.docs.map(mapDocToBooking)
        const sorted = sortBookings(bookingsData)
        setBookings(sorted)

        const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null
        setLastVisible(lastDoc)
        setHasMore(snapshot.size === PAGE_SIZE)
        setLoading(false)
      },
      (error) => {
        console.error('Error listening to bookings:', error)
        toast.error('Failed to load bookings.')
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  const loadMoreBookings = async () => {
    if (!lastVisible || loadingMore) return

    setLoadingMore(true)
    try {
      const bookingsRef = collection(db, COLLECTIONS.BOOKINGS)
      const q = query(
        bookingsRef,
        orderBy('date', 'desc'),
        startAfter(lastVisible),
        limit(PAGE_SIZE)
      )
      const snapshot = await getDocs(q)

      const moreBookings = snapshot.docs.map(mapDocToBooking)
      const combined = sortBookings([...bookings, ...moreBookings])

      setBookings(combined)

      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || lastVisible
      setLastVisible(lastDoc)
      setHasMore(snapshot.size === PAGE_SIZE)
    } catch (error) {
      console.error('Error loading more bookings:', error)
      toast.error('Failed to load previous bookings.')
    } finally {
      setLoadingMore(false)
    }
  }

  // Scroll to modal when it opens
  useEffect(() => {
    if (showModal && activeBooking) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        // Scroll window to top
        window.scrollTo({ top: 0, behavior: 'smooth' })
        
        // Also scroll the modal content to top if it has scroll
        const modalContent = document.getElementById('booking-preview-content')
        if (modalContent) {
          modalContent.scrollTop = 0
        }
      }, 100)
    }
  }, [showModal, activeBooking])

  // Scroll to top when modal opens
  useEffect(() => {
    if (showModal) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [showModal])

  // Helper function to update booking in Firestore
  const updateBookingInFirestore = async (bookingId, updates) => {
    try {
      const bookingRef = doc(db, COLLECTIONS.BOOKINGS, bookingId)
      await updateDoc(bookingRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      return true
    } catch (error) {
      console.error('Error updating booking:', error)
      toast.error('Failed to update booking. Please try again.')
      return false
    }
  }

  // Load settings from Firestore
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDocRef = doc(db, 'settings', 'booking')
        const snap = await getDoc(settingsDocRef)
        
        if (snap.exists()) {
          const data = snap.data()
          setSettings({
            postBookingIntervalMinutes: data.postBookingIntervalMinutes ?? 120,
            rules: Array.isArray(data.rules) ? data.rules : []
          })
        }
      } catch (error) {
        console.error('Failed to load settings from Firestore:', error)
      }
    }
    
    loadSettings()
  }, [])

  // Build available dates (next 30 days) excluding fully blocked days
  useEffect(() => {
    const updateDates = () => {
      const days = []
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      for (let i = 0; i < 30; i++) {
        const d = new Date(today)
        d.setDate(today.getDate() + i)
        const iso = d.toISOString().slice(0, 10)
        const weekday = d.toLocaleDateString(undefined, { weekday: 'long' })

        // Determine if the day is fully blocked by a rule spanning full day
        const fullDayBlocked = settings.rules.some((r) => {
          if (r.type === 'one_off' && r.date === iso) {
            return r.startTime === '00:00' && r.endTime === '23:59'
          }
          if (r.type === 'weekly' && r.weekday === weekday) {
            return r.startTime === '00:00' && r.endTime === '23:59'
          }
          if (r.type === 'monthly' && Number(r.dayOfMonth) === d.getDate()) {
            return r.startTime === '00:00' && r.endTime === '23:59'
          }
          if (r.type === 'daily') {
            return r.startTime === '00:00' && r.endTime === '23:59'
          }
          return false
        })

        if (!fullDayBlocked) days.push({ label: d.toDateString(), value: iso })
      }
      setAvailableDates(days)
      if (rescheduleDate && !days.find(d => d.value === rescheduleDate)) {
        setRescheduleDate(days.length > 0 ? days[0].value : '')
      }
    }
    
    updateDates()
  }, [settings.rules, rescheduleDate])

  // Build available times when reschedule date changes
  useEffect(() => {
    if (!rescheduleDate || !activeBooking) {
      setAvailableTimes([])
      return
    }
    
    const baseDuration = Number(activeBooking.duration) || 60
    const bookingFor = Number(activeBooking.bookingFor) || 1
    const durationMin = baseDuration + (bookingFor - 1) * 30
    const buffer = Number(settings.postBookingIntervalMinutes) || 0

    const opening = 0 * 60
    const closing = 24 * 60
    const weekday = getWeekdayName(rescheduleDate)

    // Collect blocked intervals
    const blocked = []
    
    // Add unavailability rules
    settings.rules.forEach((r) => {
      if (!r.startTime || !r.endTime) return
      
      const startMinutes = toMinutes(r.startTime)
      const endMinutes = toMinutes(r.endTime)
      const spansMidnight = startMinutes > endMinutes
      
      if (r.type === 'one_off' && r.date === rescheduleDate) {
        if (spansMidnight) {
          blocked.push([startMinutes, 24 * 60])
          blocked.push([0, endMinutes])
        } else {
          blocked.push([startMinutes, endMinutes])
        }
      } else if (r.type === 'weekly' && r.weekday === weekday) {
        if (spansMidnight) {
          blocked.push([startMinutes, 24 * 60])
          blocked.push([0, endMinutes])
        } else {
          blocked.push([startMinutes, endMinutes])
        }
      } else if (r.type === 'monthly' && Number(r.dayOfMonth) === new Date(rescheduleDate).getDate()) {
        if (spansMidnight) {
          blocked.push([startMinutes, 24 * 60])
          blocked.push([0, endMinutes])
        } else {
          blocked.push([startMinutes, endMinutes])
        }
      } else if (r.type === 'daily') {
        if (spansMidnight) {
          blocked.push([startMinutes, 24 * 60])
          blocked.push([0, endMinutes])
        } else {
          blocked.push([startMinutes, endMinutes])
        }
      }
    })
    
    // Fetch existing bookings for this date
    const fetchAndBlockBookings = async () => {
      try {
        const bookingsRef = collection(db, COLLECTIONS.BOOKINGS)
        const snapshot = await getDocs(bookingsRef)
        
        snapshot.docs.forEach((docSnap) => {
          const booking = docSnap.data()
          const bookingId = docSnap.id
          
          // Exclude the current booking being rescheduled
          if (booking.date === rescheduleDate && bookingId !== activeBooking.id) {
            const isUpcoming = booking.status === 'pending' || booking.status === 'rescheduled'
            
            if (isUpcoming) {
              const bookingStart = toMinutes(booking.time)
              const bookingDuration = Number(booking.duration) || durationMin
              const bookingEnd = bookingStart + bookingDuration + buffer
              blocked.push([bookingStart, bookingEnd])
            }
          }
        })
        
        const timeInterval = 30
        const times = []
        
        const today = new Date().toISOString().slice(0, 10)
        const isToday = rescheduleDate === today
        const now = new Date()
        const currentMinutes = isToday ? now.getHours() * 60 + now.getMinutes() : 0
        
        for (let start = opening; start + durationMin <= closing; start += timeInterval) {
          const end = start + durationMin
          
          if (isToday && start < currentMinutes) {
            continue
          }
          
          const isBlocked = blocked.some(([bStart, bEnd]) => overlaps(start, end, bStart, bEnd))
          
          if (!isBlocked) {
            times.push(minutesToTime(start))
          }
        }

        setAvailableTimes(times)
        if (times.length > 0 && !rescheduleTime) {
          setRescheduleTime(times[0])
        } else if (times.length === 0) {
          setRescheduleTime('')
        }
      } catch (error) {
        console.error('Error fetching bookings for date:', error)
        setAvailableTimes([])
        setRescheduleTime('')
      }
    }
    
    fetchAndBlockBookings()
  }, [rescheduleDate, activeBooking, settings.postBookingIntervalMinutes, settings.rules, bookings])

  const categories = [
    'Massage',
    'Facials',
    'Wax',
    'Body Scrubbing',
    'Manicures',
    'Pedicures',
    'Combo Packages'
  ]

  const markCompletedLabel =
    activeBooking && activeBooking.status === 'completed'
      ? 'Mark as Uncompleted'
      : 'Mark Completed'

  const isBalanceActionVisible = !!(activeBooking && Number(activeBooking.balance) > 0)
  const isPaymentCompleted = !!(activeBooking && activeBooking.paymentCompleted)

  return (
    <div className="space-y-6 text-white">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Bookings Management</h1>
          <p className="text-gray-300 mt-1">View and manage customer bookings</p>
        </div>
      </div>
      <PageHelpButton
        videoId={1}
        title="Bookings page tutorial"
        youtubeUrl="https://www.youtube.com/embed/T5_SgFQz1Ro"
      />

      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by booking code, name, phone, category, service..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-slate-900/80 border border-purple-800/40 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        {searchQuery.trim() && (
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Filters (dropdown) */}
      <div className="bg-slate-900/80 bg-opacity-70 backdrop-blur-md rounded-xl border border-purple-800/40 shadow-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setFilterOpen(prev => !prev)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-800/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Filter className="w-4 h-4 text-purple-300" />
            <span className="text-sm font-medium text-gray-200">Filters</span>
            {(filterCategory !== 'All' || filterStatus !== 'All' || filterDate || filterPayment !== 'All') && (
              <span className="text-xs text-purple-300">(active)</span>
            )}
          </div>
          {filterOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {filterOpen && (
          <div className="border-t border-slate-800/80 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Category</label>
                <select
                  value={filterCategory}
                  onChange={(e)=>setFilterCategory(e.target.value)}
                  className="w-full input-field bg-slate-950/70 border-slate-700 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option>All</option>
                  {categories.map(c => (<option key={c}>{c}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e)=>setFilterStatus(e.target.value)}
                  className="w-full input-field bg-slate-950/70 border-slate-700 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option>All</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Payment</label>
                <select
                  value={filterPayment}
                  onChange={(e)=>setFilterPayment(e.target.value)}
                  className="w-full input-field bg-slate-950/70 border-slate-700 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option>All</option>
                  <option value="completed">Completed</option>
                  <option value="uncompleted">Uncompleted</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Date</label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e)=>setFilterDate(e.target.value)}
                  className="w-full input-field bg-slate-950/70 border-slate-700 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-slate-900/80 bg-opacity-70 backdrop-blur-md rounded-xl border border-purple-800/40 overflow-hidden shadow-lg">
        <div className="border-b border-slate-800/80 px-6 py-3 text-xs text-gray-400">
          <span>
            {searchQuery.trim() || filterCategory !== 'All' || filterStatus !== 'All' || filterDate || filterPayment !== 'All'
              ? `Showing ${filtered.length} booking${filtered.length !== 1 ? 's' : ''}`
              : `Showing latest ${Math.min(filtered.length, PAGE_SIZE)} bookings`}
          </span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="flex items-center space-x-2 text-gray-300">
              <Loader className="w-6 h-6 animate-spin text-purple-400" />
              <span>Loading bookings...</span>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-950/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Preview</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Service Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Booking For</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Visit Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Region</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Deposit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Deposited</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Deposit Txn Ref</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Balance Txn Ref</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Payment Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Use Gift Card</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Pay Deposit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/80">
                {filtered.map(b => (
                  <tr key={b.id} className="hover:bg-slate-900/80 bg-slate-950/70">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button onClick={()=>openPreview(b)} className="text-purple-400 hover:text-purple-300">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{b.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.category}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.serviceType}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">GHS {Number(b.price || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.duration}m</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {b.bookingFor || 1} {b.bookingFor === 1 ? 'Person' : 'People'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.time}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 capitalize">{b.visitType}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.region || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.address || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">GHS {Number(b.deposit || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${b.deposited ? 'bg-emerald-900/80 text-emerald-200' : 'bg-amber-900/80 text-amber-200'}`}>
                        {b.deposited ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.depositTransactionRef || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">GHS {Number(b.balance || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{b.balanceTransactionRef || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${b.paymentCompleted ? 'bg-emerald-900/80 text-emerald-200' : 'bg-amber-900/80 text-amber-200'}`}>
                        {b.paymentCompleted ? 'Completed' : 'Uncompleted'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${b.useGiftCard ? 'bg-purple-900/80 text-purple-200' : 'bg-slate-800/80 text-gray-200'}`}>
                        {b.useGiftCard ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${b.payDeposit ? 'bg-blue-900/80 text-blue-200' : 'bg-slate-800/80 text-gray-200'}`}>
                        {b.payDeposit ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        b.status === 'completed'
                          ? 'bg-emerald-900/80 text-emerald-200'
                          : b.status === 'rescheduled'
                          ? 'bg-blue-900/80 text-blue-200'
                          : 'bg-slate-800/80 text-gray-200'
                      }`}>
                        {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Load previous bookings */}
        <div className="border-t border-slate-800/80 px-6 py-3 flex items-center justify-between bg-slate-950/80">
          <p className="text-xs text-gray-400">
            Need to see earlier bookings? Load previous records only when necessary.
          </p>
          <button
            onClick={loadMoreBookings}
            disabled={!hasMore || loadingMore}
            className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium bg-slate-900 hover:bg-slate-800 text-purple-200 border border-purple-800/60 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingMore ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Loading previous...</span>
              </>
            ) : (
              <>
                <span>Load previous bookings</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview / Manage Modal - portaled so header is below top bar */}
      {showModal && activeBooking && createPortal(
        <div
          id="booking-preview-modal"
          className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-start justify-center p-4 pt-20 overflow-y-auto"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-slate-900 rounded-xl shadow-2xl max-w-2xl w-full border border-purple-800/60 flex flex-col my-auto max-h-[calc(100vh-5rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-purple-800/60 flex items-center justify-between flex-shrink-0 sticky top-0 bg-slate-900 z-10">
              <h3 className="text-lg font-semibold text-white">Manage Booking</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1" id="booking-preview-content">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-sm text-gray-300">
                  <strong className="text-white">ID:</strong> {activeBooking.id}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Name:</strong> {activeBooking.name}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Phone:</strong> {activeBooking.phone}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Category:</strong> {activeBooking.category}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Service Type:</strong> {activeBooking.serviceType}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Price:</strong> GHS {Number(activeBooking.price || 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Duration:</strong> {activeBooking.duration}m
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Booking For:</strong>{' '}
                  {activeBooking.bookingFor || 1}{' '}
                  {activeBooking.bookingFor === 1 ? 'Person' : 'People'}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Date:</strong> {activeBooking.date}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Time:</strong> {activeBooking.time}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Visit Type:</strong> {activeBooking.visitType}
                </div>
                {activeBooking.visitType === 'mobile' && (
                  <>
                    <div className="text-sm text-gray-300">
                      <strong className="text-white">Region:</strong> {activeBooking.region || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-300">
                      <strong className="text-white">Address:</strong> {activeBooking.address || 'N/A'}
                    </div>
                  </>
                )}
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Deposit:</strong> GHS {Number(activeBooking.deposit || 0).toFixed(2)}{' '}
                  ({activeBooking.deposited ? 'Paid' : 'Unpaid'})
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Deposit Txn Ref:</strong>{' '}
                  {activeBooking.depositTransactionRef || 'N/A'}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Balance:</strong> GHS {Number(activeBooking.balance || 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Balance Txn Ref:</strong>{' '}
                  {activeBooking.balanceTransactionRef || 'N/A'}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Payment Status:</strong>{' '}
                  <span className={activeBooking.paymentCompleted ? 'text-emerald-400' : 'text-amber-400'}>
                    {activeBooking.paymentCompleted ? 'Completed' : 'Uncompleted'}
                  </span>
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Service Status:</strong>{' '}
                  <span
                    className={
                      activeBooking.status === 'completed'
                        ? 'text-emerald-400'
                        : activeBooking.status === 'rescheduled'
                        ? 'text-blue-400'
                        : 'text-gray-300'
                    }
                  >
                    {activeBooking.status
                      ? activeBooking.status.charAt(0).toUpperCase() + activeBooking.status.slice(1)
                      : 'Pending'}
                  </span>
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Use Gift Card:</strong>{' '}
                  {activeBooking.useGiftCard ? 'Yes' : 'No'}
                </div>
                <div className="text-sm text-gray-300">
                  <strong className="text-white">Pay Deposit:</strong>{' '}
                  {activeBooking.payDeposit ? 'Yes' : 'No'}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 flex-wrap">
                {isBalanceActionVisible && (
                  <button
                    onClick={isPaymentCompleted ? markAsUnpaid : markAsPaid}
                    disabled={loadingMarkAsPaid}
                    className="btn-primary inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingMarkAsPaid ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <DollarSign className="w-4 h-4" />
                        <span>{isPaymentCompleted ? 'Mark as Unpaid' : 'Mark as Paid'}</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={activeBooking.status === 'completed' ? markNotCompleted : markCompleted}
                  disabled={loadingMarkCompleted}
                  className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMarkCompleted ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>{markCompletedLabel}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowRescheduleModal(true)}
                  className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reschedule
                </button>
                <button
                  onClick={async () => {
                    if (!activeBooking || loadingReviewLink) return
                    const phone = normalizeGhanaPhone(activeBooking.phone)
                    if (!phone || !activeBooking.name) {
                      toast.error('Cannot send review link. Missing valid phone or name.')
                      return
                    }
                    setLoadingReviewLink(true)
                    try {
                      const functionsInstance = getFunctions(app)
                      const sendCompletion = httpsCallable(functionsInstance, 'sendCompletionSMS')
                      await sendCompletion({
                        name: activeBooking.name,
                        phone,
                        balance: 0,
                        bookingId: activeBooking.id
                      })
                      toast.success('Review link sent to client')
                    } catch (error) {
                      console.error('Error sending review link SMS:', error)
                      toast.error('Failed to send review link')
                    } finally {
                      setLoadingReviewLink(false)
                    }
                  }}
                  disabled={loadingReviewLink}
                  className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 hover:bg-slate-800 text-purple-200 border border-purple-700/60 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingReviewLink ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-4 h-4" />
                      <span>Send Review Link</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Reschedule Modal - portaled so header is below top bar */}
      {showModal && showRescheduleModal && activeBooking && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-70 z-[110] flex items-start justify-center p-4 pt-20 overflow-y-auto"
          onClick={() => setShowRescheduleModal(false)}
        >
          <div
            className="bg-slate-900 rounded-xl shadow-2xl max-w-lg w-full border border-purple-800/60 p-6 space-y-4 my-auto max-h-[calc(100vh-5rem)] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-white">Reschedule Booking</h3>
              <button
                onClick={() => setShowRescheduleModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-3">
              Choose a new date and time for this booking. SMS confirmation and reminders will be sent to the client.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Reschedule Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                  <select
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    className="w-full input-field bg-slate-950/80 border-slate-700 text-white pl-9 appearance-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    required
                  >
                    {availableDates.length === 0 && <option value="">Loading dates...</option>}
                    {availableDates.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Reschedule Time <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Clock className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                  <select
                    value={rescheduleTime}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                    className={`w-full input-field border-slate-700 text-white pl-9 appearance-none ${
                      availableTimes.length === 0 
                        ? 'bg-amber-900 border-amber-600' 
                        : 'bg-slate-950/80'
                    }`}
                    required
                    disabled={availableTimes.length === 0 || !rescheduleDate}
                  >
                    {!rescheduleDate && <option value="">Select date first</option>}
                    {rescheduleDate && availableTimes.length === 0 && (
                      <option value="">All times booked - Try another date</option>
                    )}
                    {availableTimes.map((t) => {
                      const [h, m] = t.split(':').map(Number)
                      let hour12, ampm
                      if (h === 0 || h === 24) {
                        hour12 = 12
                        ampm = 'AM'
                      } else if (h === 12) {
                        hour12 = 12
                        ampm = 'PM'
                      } else if (h > 12) {
                        hour12 = h - 12
                        ampm = 'PM'
                      } else {
                        hour12 = h
                        ampm = 'AM'
                      }
                      const displayTime = `${hour12}:${String(m).padStart(2, '0')} ${ampm}`
                      return <option key={t} value={t}>{displayTime}</option>
                    })}
                  </select>
                </div>
                {rescheduleDate && availableTimes.length === 0 && (
                  <div className="mt-2 p-2 bg-amber-900 bg-opacity-50 border border-amber-700 rounded text-xs text-amber-200 flex items-start">
                    <AlertCircle className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0" />
                    <span>This date is fully booked. Please select another date.</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowRescheduleModal(false)}
                className="px-4 py-2 text-sm rounded-lg border border-slate-700 text-gray-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await reschedule()
                  setShowRescheduleModal(false)
                }}
                disabled={!rescheduleDate || !rescheduleTime || availableTimes.length === 0 || loadingReschedule}
                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingReschedule ? (
                  <span className="inline-flex items-center space-x-2">
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </span>
                ) : (
                  'Confirm Reschedule'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Bookings
